/**
 * Languages offered by the report translate picker. `en` is the untranslated original.
 *
 * This is a curated shortlist of the world's most widely spoken languages (plus the
 * two primary languages for this product, English and Arabic, which are always shown
 * first). It is intentionally NOT exhaustive. The backend still accepts any other
 * well-formed language code (see
 * `SAI_Backend/src/modules/reports/translation/report-translation.constants.ts`), so
 * this list only governs what the dropdown surfaces.
 */
export interface ReportLanguageOption {
    /** ISO 639-1 base code sent as the `lang` query param. */
    code: string;
    /** English display name. */
    name: string;
    /** Native-script display name shown alongside the English name. */
    native: string;
    /** True for right-to-left scripts (backend applies RTL layout to the document). */
    rtl?: boolean;
}

/** Always pinned to the top of the picker, in this order. */
export const PRIMARY_REPORT_LANGUAGE_CODES: readonly string[] = ['en', 'ar'];

export const REPORT_LANGUAGES: readonly ReportLanguageOption[] = [
    // Primary
    { code: 'en', name: 'English', native: 'English' },
    { code: 'ar', name: 'Arabic', native: 'العربية', rtl: true },
    // Most widely spoken, roughly by number of speakers
    { code: 'zh', name: 'Chinese', native: '中文' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'pt', name: 'Portuguese', native: 'Português' },
    { code: 'ru', name: 'Russian', native: 'Русский' },
    { code: 'de', name: 'German', native: 'Deutsch' },
    { code: 'ja', name: 'Japanese', native: '日本語' },
    { code: 'ko', name: 'Korean', native: '한국어' },
    { code: 'it', name: 'Italian', native: 'Italiano' },
    { code: 'tr', name: 'Turkish', native: 'Türkçe' },
    { code: 'ur', name: 'Urdu', native: 'اردو', rtl: true },
    { code: 'fa', name: 'Persian', native: 'فارسی', rtl: true },
    { code: 'id', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'bn', name: 'Bengali', native: 'বাংলা' },
    { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
];

export const REPORT_LANGUAGE_BY_CODE: ReadonlyMap<string, ReportLanguageOption> = new Map(
    REPORT_LANGUAGES.map(lang => [lang.code, lang]),
);
